import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Icon,
  useDisclosure,
  useToast,
  Spinner,
  Link,
  Stack,
  Heading,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import actions from "../../actions";

dayjs.extend(utc);
dayjs.extend(timezone);

type TicketSalesReportType = "face_value" | "rebate";
type TicketSalesReportStatus = "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";

type TicketSalesReport = {
  id: string;
  start_at: string;
  end_at: string;
  report_type: TicketSalesReportType;
  status: TicketSalesReportStatus;
  created_at: string;
  url?: string;
  error_message?: string;
};

const formatDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateRangeIso = (
  value: string,
  organizationTimezone: string,
  endOfDay = false
) => {
  const time = endOfDay ? "23:59:59.999" : "00:00:00.000";
  return dayjs.tz(`${value} ${time}`, organizationTimezone).toISOString();
};

const REPORT_MIN_DATE = new Date("2024-08-01T00:00:00");
const REPORT_MAX_DATE = () => dayjs().add(1, "day").toDate();

export default function TicketSalesReportingPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reportType, setReportType] =
    useState<TicketSalesReportType>("face_value");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [reports, setReports] = useState<TicketSalesReport[]>([]);
  const [organizationTimezone, setOrganizationTimezone] = useState("UTC");
  const [rebateUiEnabled, setRebateUiEnabled] = useState(false);
  const defaultStartDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  })();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    defaultStartDate,
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;
  const maxSelectableDate = useMemo(() => {
    if (!startDate) {
      return REPORT_MAX_DATE();
    }
    return new Date(
      Math.min(
        dayjs(startDate).add(1, "year").toDate().getTime(),
        REPORT_MAX_DATE().getTime()
      )
    );
  }, [startDate]);

  const fetchReports = useCallback(async () => {
    setIsFetching(true);
    try {
      const response = await actions.ADMIN.fetchTicketSalesReports({
        limit: 25,
      });
      setReports(response?.reports || []);
    } catch (err) {
      console.error("Failed to fetch reports", err);
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    const fetchTimezone = async () => {
      try {
        const organization = await actions.ADMIN.fetchOrganization();
        setOrganizationTimezone(organization?.settings?.timezone || "UTC");
        setRebateUiEnabled(!!organization?.settings?.rebates_enabled);
      } catch (err) {
        console.error("Failed to fetch organization timezone", err);
      }
    };
    fetchTimezone();
  }, []);

  useEffect(() => {
    if (!rebateUiEnabled && reportType === "rebate") {
      setReportType("face_value");
    }
  }, [rebateUiEnabled, reportType]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }
    if (endDate.getTime() > maxSelectableDate.getTime()) {
      setDateRange([startDate, maxSelectableDate]);
    }
  }, [startDate, endDate, maxSelectableDate]);

  const onCreateReport = async () => {
    const isRangeTooLong =
      !!startDate &&
      !!endDate &&
      dayjs(endDate).isAfter(dayjs(startDate).add(1, "year"));
    if (!startDate || !endDate || isRangeTooLong) {
      return;
    }
    setIsLoading(true);
    try {
      await actions.ADMIN.createTicketSalesReport({
        start_at: toDateRangeIso(
          formatDateValue(startDate),
          organizationTimezone
        ),
        end_at: toDateRangeIso(
          formatDateValue(endDate),
          organizationTimezone,
          true
        ),
        report_type: reportType,
      });
      toast({
        status: "success",
        title: "Report requested",
        description: "Ticket sales report generation has started.",
      });
      onClose();
      await fetchReports();
    } catch (err) {
      console.error("Failed to request report", err);
      toast({
        status: "error",
        title: "Failed to request report",
        description: err?.message || "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatReportType = (reportType: TicketSalesReportType) =>
    reportType === "face_value" ? "Face Value" : "Rebate";

  const formatInOrganizationTimezone = (
    value: string,
    includeTime: boolean = false
  ) => {
    if (!value) return "-";
    return dayjs(value)
      .tz(organizationTimezone)
      .format(includeTime ? "MMM D, YYYY h:mma" : "MMM D, YYYY");
  };

  const isRangeTooLong =
    !!startDate &&
    !!endDate &&
    dayjs(endDate).isAfter(dayjs(startDate).add(1, "year"));
  const isDateRangeInvalid =
    !startDate ||
    !endDate ||
    startDate.getTime() > endDate.getTime() ||
    isRangeTooLong;

  return (
    <VStack align="stretch" maxW={"5xl"} mx="auto" spacing={4}>
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align="center"
      >
        <Stack align="start" spacing={1}>
          <Heading fontSize="lg" fontWeight={700} color="gray.900">
            Reporting
          </Heading>
          <Text fontSize="sm" color="gray.500">
            Create and download ticket sales across all events
          </Text>
        </Stack>
        <Button
          bg="black"
          color="white"
          size="sm"
          px={3}
          borderRadius="4px"
          fontWeight={600}
          _hover={{ bg: "gray.800" }}
          onClick={onOpen}
          leftIcon={<Icon as={FiPlus} />}
        >
          Create Report
        </Button>
      </Stack>

      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="8px"
        bg="white"
        overflow="hidden"
      >
        <HStack
          px={4}
          py={2.5}
          justify="space-between"
          borderBottomWidth="1px"
          borderColor="gray.100"
        >
          <Text fontSize="12px" fontWeight={600} color="gray.700">
            Report History
          </Text>
          <Text fontSize="11px" color="gray.500">
            Latest 25 requests
          </Text>
        </HStack>

        {isFetching ? (
          <VStack py={12} spacing={2}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.500">
              Loading reports...
            </Text>
          </VStack>
        ) : null}

        {!isFetching && reports.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Text fontSize="sm" fontWeight={600} color="gray.700">
              No reports yet
            </Text>
            <Text fontSize="sm" color="gray.500">
              Click &quot;Create Report&quot; to create your first export.
            </Text>
          </VStack>
        ) : null}

        {!isFetching && reports.length > 0 ? (
          <Box
            overflowX="auto"
            overflowY="hidden"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <Table
              size="md"
              variant="simple"
              minW="860px"
              sx={{ tableLayout: "fixed" }}
            >
              <Thead>
                <Tr bg="gray.50">
                  <Th
                    w="300px"
                    py={2.5}
                    fontSize="10px"
                    color="gray.500"
                    letterSpacing="0.04em"
                  >
                    DATE RANGE
                  </Th>
                  <Th
                    w="160px"
                    py={2.5}
                    fontSize="10px"
                    color="gray.500"
                    letterSpacing="0.04em"
                  >
                    REPORT TYPE
                  </Th>
                  <Th
                    w="250px"
                    py={2.5}
                    fontSize="10px"
                    color="gray.500"
                    letterSpacing="0.04em"
                  >
                    REQUESTED AT
                  </Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {reports.map((report) => (
                  <Tr key={report.id} _hover={{ bg: "gray.50" }} h="55px">
                    <Td py={2.5}>
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        fontWeight={600}
                        noOfLines={1}
                      >
                        {formatInOrganizationTimezone(report.start_at)} -{" "}
                        {formatInOrganizationTimezone(report.end_at)}
                      </Text>
                    </Td>
                    <Td py={2.5}>
                      <Text fontSize="sm" color="gray.800">
                        {formatReportType(report.report_type)}
                      </Text>
                    </Td>

                    <Td py={2.5}>
                      <Text fontSize="sm" color="gray.700">
                        {formatInOrganizationTimezone(report.created_at, true)}
                      </Text>
                    </Td>
                    <Td textAlign="right" py={2.5}>
                      {report.status === "QUEUED" ? (
                        <Badge
                          bg="gray.100"
                          color="gray.700"
                          borderRadius="8px"
                          px={2.5}
                          py={0.5}
                          fontSize="xs"
                          fontWeight={600}
                          textTransform="none"
                        >
                          Queued
                        </Badge>
                      ) : null}
                      {report.status === "PROCESSING" ? (
                        <HStack spacing={2} w="100%" justify="flex-end">
                          <Spinner size="xs" color="blue.500" />
                          <Text fontSize="xs" color="blue.700" fontWeight={600}>
                            Processing
                          </Text>
                        </HStack>
                      ) : null}
                      {report.status === "FAILED" ? (
                        <Badge
                          bg="red.50"
                          color="red.700"
                          borderColor="red.200"
                          borderWidth="1px"
                          borderRadius="8px"
                          px={2.5}
                          py={0.5}
                          fontSize="xs"
                          fontWeight={600}
                          textTransform="none"
                        >
                          Failed
                        </Badge>
                      ) : null}
                      {report.status === "COMPLETED" && report.url ? (
                        <Button
                          as={Link}
                          href={report.url}
                          isExternal
                          h="35px"
                          px={2.5}
                          variant="outline"
                          borderColor="gray.300"
                          color="gray.700"
                          fontSize="xs"
                          fontWeight={600}
                          boxShadow="0px 1px 2px rgba(0, 0, 0, 0.06)"
                          _hover={{ bg: "gray.50" }}
                        >
                          Download
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : null}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent backgroundColor="#FAFAFA" borderRadius="8px">
          <ModalHeader pt={4} px={4} fontSize="lg">
            Create Report
          </ModalHeader>
          <ModalBody
            backgroundColor="white"
            rounded="8px"
            borderColor="#DDDDDD"
            borderWidth="1px"
            mx={5}
            px={5}
            py={5}
          >
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="md" fontWeight={600} color="gray.700">
                  Report Type
                </FormLabel>
                <Select
                  size="md"
                  h="40px"
                  boxShadow="0px 1px 2px rgba(0, 0, 0, 0.08)"
                  value={reportType}
                  onChange={(e) =>
                    setReportType(e.target.value as TicketSalesReportType)
                  }
                >
                  <option value="face_value">Face Value</option>
                  {rebateUiEnabled ? (
                    <option value="rebate">Rebate</option>
                  ) : null}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="md" fontWeight={600} color="gray.700">
                  Date Range
                </FormLabel>
                <Box
                  w="100%"
                  sx={{
                    ".react-datepicker-wrapper": {
                      width: "100%",
                    },
                    ".react-datepicker__input-container": {
                      width: "100%",
                    },
                  }}
                >
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    minDate={REPORT_MIN_DATE}
                    maxDate={maxSelectableDate}
                    onChange={(update) =>
                      setDateRange(update as [Date | null, Date | null])
                    }
                    dateFormat="MMMM do yyyy"
                    customInput={
                      <Input
                        h="40px"
                        w="100%"
                        fontSize="14px"
                        boxShadow="0px 1px 2px rgba(0, 0, 0, 0.08)"
                        placeholder="Select date range"
                      />
                    }
                  />
                </Box>
              </FormControl>
              {isDateRangeInvalid ? (
                <Text fontSize="sm" color="red.500">
                  {isRangeTooLong
                    ? "Date range cannot exceed 1 year."
                    : "Please select a valid start and end date."}
                </Text>
              ) : null}
            </VStack>
          </ModalBody>
          <ModalFooter pt={3} pb={3} px={4}>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                size="md"
                fontSize="sm"
                borderWidth="1px"
                borderColor="gray.300"
                color="gray.700"
                _hover={{ bg: "gray.50" }}
                onClick={onClose}
                fontWeight={500}
              >
                Cancel
              </Button>
              <Button
                bg="black"
                color="white"
                size="md"
                fontSize="sm"
                px={3}
                onClick={onCreateReport}
                isLoading={isLoading}
                isDisabled={isDateRangeInvalid}
                _hover={{ bg: "gray.800" }}
              >
                Create Report
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
